"use client"

import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@ui/shad"
import { AlertTriangle, Loader2 } from "lucide-react"

import { useSaveUserMapping, useUserMappingProposal } from "@/hooks/api/data-imports/user-mappings"

interface MapTeamStepProps {
  dataImportId?: string
  dataImportSourceId?: string
  onNext: () => void
  onReadyChange?: (ready: boolean) => void
}

export interface MapTeamStepRef {
  submit: () => Promise<void>
  isPending: boolean
}

export const MapTeamStep = forwardRef<MapTeamStepRef, MapTeamStepProps>(function MapTeamStep(
  { dataImportId, dataImportSourceId, onNext, onReadyChange },
  ref
) {
  const { data: proposal, isLoading, isError } = useUserMappingProposal({ dataImportId, dataImportSourceId })
  const saveUserMapping = useSaveUserMapping({ dataImportId, dataImportSourceId })

  // Let the wizard disable Continue until the roster has loaded (or errored).
  useEffect(() => {
    onReadyChange?.(!isLoading)
  }, [isLoading, onReadyChange])

  // Picked teammate email per redtail_user_id ("" = unmapped → falls back to the principal).
  const [selected, setSelected] = useState<Record<string, string>>({})
  const [initialized, setInitialized] = useState(false)

  const bookUserEmails = useMemo(() => new Set((proposal?.book_users ?? []).map((user) => user.email)), [proposal])

  useEffect(() => {
    if (initialized || !proposal) return

    const initial: Record<string, string> = {}
    proposal.users.forEach((user) => {
      // Pre-select the importer when their login email matches a teammate.
      initial[user.redtail_user_id] =
        user.suggested_email && bookUserEmails.has(user.suggested_email) ? user.suggested_email : ""
    })
    setSelected(initial)
    setInitialized(true)
  }, [proposal, initialized, bookUserEmails])

  useImperativeHandle(
    ref,
    () => ({
      submit: async () => {
        // No-op while the roster is still loading so a fast click can't skip the
        // step by posting an empty mapping.
        if (isLoading) return

        // If the roster failed to load there's nothing sensible to save (and an
        // empty save could wipe a previously stored mapping) — just advance.
        if (isError || !proposal) {
          onNext()
          return
        }

        const userMappings = Object.entries(selected)
          .filter(([, email]) => email.length > 0)
          .map(([redtail_user_id, email]) => ({ redtail_user_id, email }))

        await saveUserMapping.mutateAsync(userMappings)
        onNext()
      },
      isPending: saveUserMapping.isPending,
    }),
    [selected, saveUserMapping, onNext, isLoading, isError, proposal]
  )

  if (isLoading) {
    return (
      <div className="text-muted-foreground flex items-center justify-center py-12">
        <Loader2 className="mr-2 animate-spin" /> Loading your Redtail users…
      </div>
    )
  }

  if (isError || !proposal) {
    return (
      <div className="text-muted-foreground mx-auto max-w-xl rounded-md border p-4 text-sm">
        We couldn&apos;t load your Redtail users. Make sure your Redtail login was provided for this import, then try
        again.
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div>
        <h2 className="text-lg font-medium">Map your team</h2>
        <p className="text-muted-foreground mt-1 text-sm">
          Match each Redtail user to their Slant teammate so their tasks are assigned to the right person.
        </p>
      </div>

      <div className="text-muted-foreground flex items-start gap-2 rounded-md border p-3 text-sm">
        <AlertTriangle className="mt-0.5 shrink-0" />
        <span>
          Don&apos;t see a teammate in the list? They haven&apos;t joined Slant yet — invite them and have them accept
          before importing. Until then, their tasks are assigned to you.
        </span>
      </div>

      <div className="space-y-3">
        {proposal.users.map((user) => (
          <div key={user.redtail_user_id} className="flex items-center gap-3">
            <div className="w-48 shrink-0 text-sm">
              <span className="font-medium">{user.name}</span>
              {user.is_importer && <span className="text-muted-foreground ml-2 text-xs">(you)</span>}
            </div>
            <Select
              value={selected[user.redtail_user_id] || undefined}
              onValueChange={(value) => setSelected((prev) => ({ ...prev, [user.redtail_user_id]: value }))}
            >
              <SelectTrigger className="flex-1">
                <SelectValue placeholder="Select a teammate" />
              </SelectTrigger>
              <SelectContent>
                {proposal.book_users.map((teammate) => (
                  <SelectItem key={teammate.email} value={teammate.email}>
                    {teammate.name} ({teammate.email})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ))}
      </div>
    </div>
  )
})
